import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, X, Search, AlertTriangle } from 'lucide-react';
import { cn } from 'lib/utils';
import { M } from './theme';
import { useMobileDockOverlay } from './MobileShell';

/** Keyboard activation (Enter/Space) for elements using `role="button"`. */
function handleActivateKeyDown(onActivate: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };
}

/** Shared phone-screen shell pieces. Each screen owns its header — the global app header is hidden on mobile. */

/** Pinned with `absolute inset-0` — the reliable way to get a true viewport-height screen on mobile. */
export function MobileScreen({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('absolute inset-0 flex flex-col overflow-hidden', className)} style={{ background: M.bg }}>
      {children}
    </div>
  );
}

/** White screen header — title on the left, optional actions on the right. */
export function ScreenHeader({
  title,
  count,
  right,
  children,
  onBack,
}: {
  title: React.ReactNode;
  count?: React.ReactNode;
  right?: React.ReactNode;
  /** Optional tab strip / toolbar rendered under the title row. */
  children?: React.ReactNode;
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex-shrink-0" style={{ background: M.card, borderBottom: `1px solid ${M.border}` }}>
      <div className="flex items-center gap-2.5 px-4 pt-3.5" style={{ paddingBottom: children ? 0 : 14 }}>
        {onBack && (
          // 44px tap target via padding, pulled back with negative margins so
          // the visible icon keeps its original position/size.
          <button
            type="button"
            onClick={onBack}
            aria-label={t('back', 'Back')}
            className="-ml-2 -my-2 flex h-11 w-11 items-center justify-center rounded-md"
          >
            <ArrowLeft className="h-[17px] w-[17px]" style={{ color: M.ink2 }} />
          </button>
        )}
        <span className="min-w-0 truncate text-[17px] font-bold tracking-[-0.01em]" style={{ color: M.ink }}>
          {title}
        </span>
        {count != null && (
          <span
            className="inline-flex h-5 items-center rounded-md px-[7px] text-[11.5px] font-medium tabular-nums"
            style={{ background: M.muted, color: M.ink3 }}
          >
            {count}
          </span>
        )}
        {right && <div className="ml-auto flex items-center gap-1.5">{right}</div>}
      </div>
      {children && <div className="px-4">{children}</div>}
    </div>
  );
}

/**
 * A 36px ghost icon button for the screen header (search, filters), with a 44px
 * tap area. `dot` marks a set state, e.g. filters applied behind the icon.
 */
export function HeaderIcon({
  icon,
  label,
  onClick,
  dot = false,
  pressed,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  dot?: boolean;
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      className="-m-1 flex h-11 w-11 items-center justify-center p-1"
    >
      <span
        className="relative flex h-9 w-9 items-center justify-center rounded-lg"
        style={{ color: M.ink2, background: pressed ? M.muted : undefined }}
      >
        {icon}
        {dot && (
          <span
            aria-hidden
            className="absolute right-[7px] top-[7px] h-[7px] w-[7px] rounded-full"
            style={{ background: M.ink, boxShadow: `0 0 0 2px ${M.card}` }}
          />
        )}
      </span>
    </button>
  );
}

/** Tap area grown to 44px via padding + a negative margin, so the header layout doesn't shift. */
export function HeaderPlus({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="-m-1 flex h-11 w-11 items-center justify-center p-1"
    >
      {/* Creating is the screen's main action, so it takes the lime primary. */}
      <span
        className="flex h-9 w-9 items-center justify-center rounded-lg"
        style={{ background: M.primary, color: M.primaryInk }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </span>
    </button>
  );
}

/** Scrollable content body with the prototype's default padding. */
export function ScreenBody({
  children,
  className,
  pad = true,
}: {
  children: React.ReactNode;
  className?: string;
  pad?: boolean;
}) {
  return (
    <div
      className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-thin', pad && 'px-4 pt-3', className)}
      // Extra bottom padding so the last card clears the FLOATING capsule nav
      // (66px tall, 14px from the bottom) and the iOS home indicator.
      style={pad ? { paddingBottom: 'calc(92px + env(safe-area-inset-bottom))' } : undefined}
    >
      {children}
    </div>
  );
}

/** Font pinned ≥16px so iOS Safari doesn't zoom on focus (matches the app-wide input guard). */
export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Focus on mount — for a field revealed by the header's search icon. */
  autoFocus?: boolean;
}) {
  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: M.faint }} />
      <input
        // eslint-disable-next-line jsx-a11y/no-autofocus -- opened on request by the header's search button
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-[38px] w-full rounded-md pl-9 pr-3 text-[16px] outline-none"
        style={{ border: `1px solid ${M.border}`, background: M.card, color: M.ink }}
      />
    </div>
  );
}

/** Section heading above grouped content, in sentence case. */
export function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('mb-2 text-[13px] font-semibold', className)} style={{ color: M.ink2 }}>
      {children}
    </p>
  );
}

/** A tab or segment; `count` shows after the label in a lighter weight. */
export type ScrollTab = { value: string; label: string; count?: number | string };

/**
 * Underline tabs that scroll sideways (status, workspace and group-detail tabs):
 * 44px, 14px, the current tab ink with an ink underline. The right edge fades
 * so a clipped tab reads as "scroll for more".
 */
export function ScrollTabs({
  tabs,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: {
  tabs: ScrollTab[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex gap-5 overflow-x-auto scrollbar-none [-webkit-mask-image:linear-gradient(90deg,#000_90%,transparent)] [mask-image:linear-gradient(90deg,#000_90%,transparent)]',
        className,
      )}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className="inline-flex h-11 flex-shrink-0 items-center gap-1 whitespace-nowrap text-[14px] transition-colors"
            style={{
              fontWeight: active ? 600 : 500,
              color: active ? M.ink : M.ink3,
              boxShadow: active ? `inset 0 -2px 0 ${M.ink}` : 'none',
            }}
          >
            {tab.label}
            {tab.count != null && (
              <span className="tabular-nums" style={{ fontWeight: 400, color: M.ink3 }}>
                {' '}
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Segmented control on a grey track (views, periods, owned/shared): the chosen
 * segment is raised in white. `full` spreads the segments across the width.
 */
export function Segmented({
  tabs,
  value,
  onChange,
  full = false,
  className,
  'aria-label': ariaLabel,
}: {
  tabs: ScrollTab[];
  value: string;
  onChange: (value: string) => void;
  full?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'h-10 items-center gap-0.5 overflow-x-auto rounded-lg p-[3px] scrollbar-none',
        full ? 'flex w-full' : 'inline-flex',
        className,
      )}
      style={{ background: M.muted }}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={cn(
              'inline-flex h-full items-center justify-center gap-1 whitespace-nowrap rounded-md px-3 text-[14px] transition-colors',
              full && 'flex-1',
            )}
            style={
              active
                ? { background: M.card, color: M.ink, fontWeight: 600, boxShadow: '0 1px 2px rgba(17,24,39,.08)' }
                : { background: 'transparent', color: M.ink3, fontWeight: 500 }
            }
          >
            {tab.label}
            {tab.count != null && (
              <span className="tabular-nums" style={{ fontWeight: 400, color: M.ink3 }}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** One white card of hairline-divided rows: the phone list pattern (rows bring their own padding). */
export function ListCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn('divide-y divide-[#E5E7EB] overflow-hidden rounded-lg', className)}
      style={{ background: M.card, border: `1px solid ${M.border}` }}
    >
      {children}
    </div>
  );
}

/** A tappable white card with the prototype's border/radius. */
export function MCard({
  children,
  onClick,
  className,
  style,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? handleActivateKeyDown(onClick) : undefined}
      className={cn('rounded-lg', onClick && 'cursor-pointer', className)}
      style={{ background: M.card, border: `1px solid ${M.border}`, ...style }}
    >
      {children}
    </div>
  );
}

/** Grouped rows list — white rounded container with hairline dividers. */
export function RowGroup({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn('overflow-hidden rounded-md', className)}
      style={{ background: M.card, border: `1px solid ${M.border}` }}
    >
      {children}
    </div>
  );
}
export function Row({
  children,
  onClick,
  last,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  last?: boolean;
  className?: string;
}) {
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? handleActivateKeyDown(onClick) : undefined}
      className={cn('flex items-center gap-2 px-3 py-[11px]', onClick && 'cursor-pointer', className)}
      style={{ borderBottom: last ? 'none' : `1px solid ${M.divider}` }}
    >
      {children}
    </div>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Dimmed backdrop + rounded panel with a grab handle. Used for create flows, filters and previews.
 * A modal dialog: focus moves in on open (unless a child autofocused) and back on close,
 * Tab stays inside, Escape and the backdrop close it.
 */
export function BottomSheet({
  open,
  onClose,
  children,
  maxHeight,
  label,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxHeight?: string;
  /** Accessible name when the sheet has no heading of its own. */
  label?: string;
}) {
  const sheetRef = React.useRef<HTMLDivElement>(null);
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  });

  React.useEffect(() => {
    if (!open) return undefined;
    const sheet = sheetRef.current;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (sheet && !sheet.contains(document.activeElement)) sheet.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !sheet) return;
      const items = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        sheet.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === sheet)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !sheet.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previous && previous.isConnected) previous.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0 z-[40] animate-in fade-in-0"
        style={{ background: 'rgba(17,24,39,.4)' }}
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="absolute inset-x-0 bottom-0 z-[41] animate-in slide-in-from-bottom-4 overflow-y-auto rounded-t-2xl px-[18px] pb-6 pt-4 outline-none"
        style={{
          background: M.card,
          boxShadow: '0 -8px 24px rgba(17,24,39,.15)',
          maxHeight: maxHeight || '85%',
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
        }}
      >
        <div className="mx-auto mb-4 h-1 w-9 rounded-full" style={{ background: M.chevron }} />
        {children}
      </div>
    </>
  );
}

/** Full-screen overlay for the grader, question editor, respondent detail and group drill-downs. */
export function FullScreen({
  open,
  onClose,
  title,
  right,
  children,
  footer,
  closeIcon = 'back',
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  /** Pinned action bar below the scroll area (e.g. a filter "Apply" button). */
  footer?: React.ReactNode;
  closeIcon?: 'back' | 'x';
}) {
  const { t } = useTranslation();
  // Hide the bottom dock while this overlay is open so it doesn't stay
  // tappable underneath the overlay's own action bar (e.g. the grader).
  useMobileDockOverlay(open);
  if (!open) return null;
  return (
    <div
      className="absolute inset-0 z-[45] flex animate-in slide-in-from-right-4 flex-col"
      style={{ background: M.card }}
    >
      <div
        className="flex h-12 flex-shrink-0 items-center gap-2.5 px-3.5"
        style={{ borderBottom: `1px solid ${M.border}` }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close', 'Close')}
          className="-ml-2 flex h-11 w-11 items-center justify-center rounded-md"
        >
          {closeIcon === 'x' ? (
            <X className="h-[17px] w-[17px]" style={{ color: M.ink2 }} />
          ) : (
            <ArrowLeft className="h-[17px] w-[17px]" style={{ color: M.ink2 }} />
          )}
        </button>
        <span className="min-w-0 truncate text-[13.5px] font-bold" style={{ color: M.ink }}>
          {title}
        </span>
        {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">{children}</div>
      {footer && (
        <div
          className="flex-shrink-0 px-3.5 pt-3"
          style={{
            borderTop: `1px solid ${M.border}`,
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

/** Small circular monogram avatar (initials). */
export function Monogram({
  initials,
  size = 28,
  bg = M.muted,
  color = M.ink2,
}: {
  initials: string;
  size?: number;
  bg?: string;
  color?: string;
}) {
  return (
    <span
      className="flex flex-shrink-0 items-center justify-center rounded-full font-bold"
      style={{ width: size, height: size, background: bg, color, fontSize: size * 0.4 }}
    >
      {initials}
    </span>
  );
}

/** Status dot + label used in list rows. */
export function StatusPill({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex flex-shrink-0 items-center gap-1 text-[11px] font-semibold" style={{ color }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

/** Loading skeleton block matching the mobile card rhythm. */
export function MobileSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2.5 px-3.5 py-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-[76px] w-full animate-pulse rounded-lg" style={{ background: M.muted }} />
      ))}
    </div>
  );
}

/** Centered empty / info state. */
export function MobileEmpty({
  icon,
  title,
  description,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
      {icon && (
        <div
          aria-hidden
          className="mb-2.5 flex h-10 w-10 items-center justify-center rounded-full [&_svg]:h-[18px] [&_svg]:w-[18px]"
          style={{ background: M.muted, color: M.ink3 }}
        >
          {icon}
        </div>
      )}
      <p className="text-[15px] font-semibold leading-[22px]" style={{ color: M.ink }}>
        {title}
      </p>
      {description && (
        <p className="mt-1 max-w-[300px] text-[13.5px] leading-5" style={{ color: M.ink3 }}>
          {description}
        </p>
      )}
      {action && <div className="mt-3.5">{action}</div>}
    </div>
  );
}

/** Distinct from `MobileEmpty` (red glyph + retry) so a failed fetch never reads as "nothing here". */
export function MobileError({
  title,
  description,
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center" role="alert">
      <div
        aria-hidden
        className="mb-2.5 flex h-10 w-10 items-center justify-center rounded-full"
        style={{ background: M.muted, color: M.red }}
      >
        <AlertTriangle size={18} />
      </div>
      <p className="text-[15px] font-semibold leading-[22px]" style={{ color: M.ink }}>
        {title ?? t('something_went_wrong', 'Something went wrong')}
      </p>
      <p className="mt-1 max-w-[300px] text-[13.5px] leading-5" style={{ color: M.ink3 }}>
        {description ?? t('failed_to_load', "Couldn't load")}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3.5 h-10 rounded-md px-4 text-[14px] font-medium"
          style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
        >
          {t('retry', 'Retry')}
        </button>
      )}
    </div>
  );
}
